import { Component, OnDestroy, OnInit } from "@angular/core"
import { Title } from "@angular/platform-browser"
import { ActivatedRoute, Params, Router } from "@angular/router"
import { combineLatest, Observable, Subject } from "rxjs"
import { Attribute } from "../../models/attribute.model"
import { CollectionNode } from "../../models/collectionNode.model"
import { PBSLM_TITLE } from "../../models/constants"
import { StandardCollectionGTM } from "../../models/gtm/collection"
import { CollectionService } from "../../services/collection.service"
import { ColorCodeService } from "../../services/color-code.service"
import { GTMService } from "../../services/gtm.service"
import { UserService } from "../../services/user.service"
import { TruncatePipe } from "../../shared-components/pipes/truncate.pipe"
import { filter, map, switchMap, takeUntil, tap } from "rxjs/operators"
import { StudentGateService } from "../../services/student-gate.service"
import Utils from "../../utils/utils"
import { PreviewService } from "../../services/preview.service"
import { routeAnimations, routeTransition } from "../../utils/animations"

declare var window: any

@Component({
    selector: "app-collection-standard",
    templateUrl: "./collection-standard.component.html",
    styleUrls: ["./collection-standard.component.scss"],
    host: { "[@routeTransition]": "true", "[@routeAnimations]": "false", style: "display: block;" },
    animations: [routeTransition, routeAnimations],
    standalone: false,
})
export class CollectionStandardComponent implements OnInit, OnDestroy {
    collections: CollectionNode = new CollectionNode(null)
    activeCollection: CollectionNode
    bannerAddress: string = ""
    attributions: Attribute[] = []
    founder: Attribute[] = []
    isForkRequestInProgress: boolean = false
    isStudent: boolean = false
    params: Params = {}
    selectedGrade: string = ""
    selectedFilter: string = ""
    shouldShowEmptyMessage: boolean = false
    isLoading: boolean = true
    isCollectionPreview: boolean

    protected gtmPrefix: string = "gtm-collection"
    private destroyed$: Subject<void> = new Subject<void>()

    studentToggleObservable: Observable<Params> = combineLatest([
        this.studentGateService.collectionFilterChoice,
        this.studentGateService.selectedGradeState,
    ]).pipe(
        takeUntil(this.destroyed$),
        tap(
            ([collectionFilterChoice, selectedGradeState]: [string, string]) =>
                (this.selectedFilter = selectedGradeState)
        ),
        filter(
            ([collectionFilterChoice, selectedGradeState]: [string, string]) =>
                (collectionFilterChoice || selectedGradeState) && !!this.activeCollection?.slug
        ),
        switchMap(() => this.route.params),
        tap((params: Params) => {
            this.isLoading = true
            const { topParentSlug = "", parentSlug = "", slug } = params
            this.collectionService.requestCollections(
                `${topParentSlug ? topParentSlug + "/t/" : ""}` +
                    `${parentSlug ? parentSlug + "/" : ""}` +
                    `${slug}`,
                true
            )
        })
    )

    routeParamsObservable: Observable<Params> = this.route.params.pipe(
        takeUntil(this.destroyed$),
        tap((params: Params) => {
            const { topParentSlug = "", parentSlug = "", slug } = params
            this.collectionService.requestCollections(
                `${topParentSlug ? topParentSlug + "/t/" : ""}` +
                    `${parentSlug ? parentSlug + "/" : ""}` +
                    `${slug}`
            )
        })
    )

    collectionChangeObservable: Observable<CollectionNode> =
        this.collectionService.collectionsChange.pipe(
            takeUntil(this.destroyed$),
            tap((collections: CollectionNode) => {
                if (collections.isPremium) {
                    this.router.navigate([collections.getRouterLink()], {
                        queryParams: this.params,
                    })
                }
                this.collections = new CollectionNode(collections)
                this.bannerAddress = collections.banner
            })
        )

    activeCollectionChangeObservable: Observable<CollectionNode> =
        this.collectionService.activeCollectionsChange.pipe(
            takeUntil(this.destroyed$),
            filter((collection: CollectionNode) => !!collection),
            tap((activeCollection: CollectionNode) => this.setPageData(activeCollection))
        )

    forkRequestObservable: Observable<boolean> = this.collectionService.forkRequestInProgress.pipe(
        takeUntil(this.destroyed$),
        tap((state: boolean) => (this.isForkRequestInProgress = state))
    )

    isLoading$: Observable<boolean> = combineLatest([
        this.collectionService.collectionsChange,
        this.route.params,
    ]).pipe(
        takeUntil(this.destroyed$),
        map(
            ([collection, params]: [CollectionNode, Params]): boolean =>
                collection?.slug === params.slug
        )
    )

    constructor(
        public collectionService: CollectionService,
        private route: ActivatedRoute,
        private router: Router,
        private colorCodeService: ColorCodeService,
        private titleService: Title,
        private gtmService: GTMService,
        private truncatePipe: TruncatePipe,
        public userService: UserService,
        private studentGateService: StudentGateService,
        private previewService: PreviewService
    ) {}

    public ngOnInit() {
        this.routeParamsObservable.subscribe()
        this.activeCollectionChangeObservable.subscribe()
        this.forkRequestObservable.subscribe()
        this.studentToggleObservable.subscribe()
        this.collectionChangeObservable.subscribe()
        this.isStudent = Utils.hasStudentExperience()
        this.selectedGrade = this.studentGateService.selectedGradeValue
        if (!!this.route.snapshot.queryParams.student) this.params.student = true
        this.isLoading$.subscribe()
        this.isCollectionPreview = this.previewService.previewOnCollection
    }

    private setPageData(activeCollection: CollectionNode) {
        if (!activeCollection.id) return
        if (!this.activeCollection || this.activeCollection.id !== activeCollection.id) {
            // This needs to be tracked every time the collection changes
            // We need to handle both initial load and navigating to an already loaded collection
            window.scroll(0, 0)
            this.setGTM(activeCollection)
            this.collectionService.incrementViewCount(activeCollection.slug)
        }
        this.activeCollection = activeCollection
        this.setWindowTitle(activeCollection)
        this.attributions = activeCollection.getContributors()
        this.founder = activeCollection.getFounders()
        this.emitAdminHeaderLinks()
    }

    private setGTM(activeCollection: CollectionNode) {
        this.gtmService.resetDataLayer()
        this.gtmService.setDataLayer(new StandardCollectionGTM(activeCollection))
        this.gtmService.pushPageView()
    }

    private emitAdminHeaderLinks(): void {
        if (!this.activeCollection) return

        const { id } = this.activeCollection
        const title: string = "Collection"
        const adminLink: string = CollectionService.getAdminLink(id)

        this.userService.emitAdminHeaderData({
            title,
            adminLink,
        })
        this.shouldShowEmptyMessage = this.shouldDisplayEmptyMessage()
    }

    public getStyle() {
        if (!this.colorCodeService.isWhite(this.collections.style.colorBkgd)) {
            return {
                background: this.colorCodeService.shadeColor(this.collections.style.colorBkgd),
            }
        }
        return null
    }

    public setWindowTitle(activeCollection: CollectionNode) {
        if (activeCollection.title) {
            const topCollection: CollectionNode = this.collections
            let titlesString: string = `${activeCollection.title}`
            if (activeCollection !== topCollection && !!topCollection) {
                titlesString = `${titlesString} | ${topCollection.title}`
            }
            const titleWithoutHtml = this.truncatePipe.transform(
                `${titlesString} | ${PBSLM_TITLE}`,
                150
            )
            this.titleService.setTitle(titleWithoutHtml)
        }
    }

    public setMaximumWidth() {
        if (!this.showSideBar()) {
            return { "max-width": "895px" }
        }
    }

    public showSideBar(): boolean {
        if (!this.activeCollection) return false
        return (
            this.collections.children.length !== 0 ||
            this.activeCollection.getFounders().length !== 0 ||
            !!this.activeCollection.credits
        )
    }

    public getDropDownNavigationNodes() {
        if (this.activeCollection.children.length) {
            return this.activeCollection.children
        }
        return this.activeCollection.parents[this.activeCollection.parents.length - 1].children
    }

    get isSubCollectionOrSingleCollectionPage(): boolean {
        return window.location.pathname.includes("/t/") || !this.activeCollection.children.length
    }

    get collectionHasResources() {
        if (!this.activeCollection?.children?.length || this.isForkRequestInProgress) return false
        return this.activeCollection.children.some(
            (node: CollectionNode) => !!node.resources.length
        )
    }

    shouldDisplayEmptyMessage() {
        return (
            !this.collectionHasResources &&
            this.isSubCollectionOrSingleCollectionPage &&
            !this.collectionService.isLoadingCollections &&
            !Object.entries(this.activeCollection.resourceMediaTypes)
        )
    }

    public ngOnDestroy() {
        this.shouldShowEmptyMessage = false
        this.userService.emitAdminHeaderData({})
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
